import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Form, Input, InputNumber, Modal,
  Popconfirm, Select, Space, Switch, Table, Tabs, Tag, Typography, message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, LockOutlined, PlusOutlined } from '@ant-design/icons';
import { ENDPOINTS } from '../../helpers/api/apiConfig';
import ModuleConfigEditor from './ModuleConfigEditor';

const { Text, Title } = Typography;

interface CapDef {
  id: number;
  key: string;
  name: string;
  description?: string | null;
  moduleType: string;
  defaultTriggerMethod: string;
  dataType: string;
  unit?: string | null;
  supportsContinuous: boolean;
  mavlinkMapping?: Record<string, any> | null;
  configSchema?: Record<string, any> | null;
  defaultConfig?: Record<string, any> | null;
  commandSpecs?: Array<Record<string, any>> | null;
  outputSchema?: Record<string, any> | null;
  icon?: string | null;
  sortOrder: number;
  isBuiltIn: boolean;
  meta?: Record<string, any> | null;
}

interface CapabilityDef {
  id: number;
  key: string;
  name: string;
  description?: string | null;
  category: string;
  operationEnvironments?: string[] | null;
  icon?: string | null;
  sortOrder: number;
  isBuiltIn: boolean;
  meta?: Record<string, any> | null;
}

interface Props {
  buildAuthHeaders: (includeContentType?: boolean) => Record<string, string>;
  onAuthMismatch: (response: Response, errorData: any) => Promise<boolean>;
}

const CAP_TYPES = [
  { value: 'sensor', label: 'Sensor' },
  { value: 'camera', label: 'Camera' },
  { value: 'actuator', label: 'Actuator' },
  { value: 'relay', label: 'Relay' },
  { value: 'communication', label: 'Communication' },
  { value: 'other', label: 'Other' },
];

const TRIGGER_METHODS = [
  { value: 'api_call', label: 'API call – OSCAR sends HTTP/MQTT to device' },
  { value: 'servo', label: 'Servo – MAVLink DO_SET_SERVO' },
  { value: 'relay', label: 'Relay – MAVLink DO_SET_RELAY' },
  { value: 'camera_shutter', label: 'Camera shutter – MAVLink DO_DIGICAM_CONTROL' },
  { value: 'cam_trigg_dist', label: 'Camera trigger distance – continuous capture' },
  { value: 'manual', label: 'Manual – operator triggers by hand' },
];

const DATA_TYPES = [
  { value: 'scalar', label: 'Scalar (single number)' },
  { value: 'multi_scalar', label: 'Multi-scalar (multiple numbers)' },
  { value: 'image', label: 'Image' },
  { value: 'spectral', label: 'Spectral (multi-band)' },
  { value: 'video', label: 'Video' },
  { value: 'none', label: 'None (actuators/relays)' },
];

const CAPABILITY_CATEGORIES = [
  { value: 'navigation', label: 'Navigation' },
  { value: 'mapping', label: 'Mapping' },
  { value: 'autonomy', label: 'Autonomy' },
  { value: 'localization', label: 'Localization' },
  { value: 'safety', label: 'Safety' },
  { value: 'payload', label: 'Payload' },
  { value: 'connectivity', label: 'Connectivity' },
  { value: 'other', label: 'Other' },
];

const OPERATION_ENVIRONMENTS = [
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'indoor', label: 'Indoor' },
  { value: 'mixed', label: 'Mixed' },
  { value: 'scan', label: 'Scan' },
];

const typeColor: Record<string, string> = {
  sensor: 'green', camera: 'blue', actuator: 'orange',
  relay: 'volcano', communication: 'purple', other: 'default',
};

const stringifyJson = (value: unknown) => {
  if (value == null) return '';
  try { return JSON.stringify(value, null, 2); } catch { return ''; }
};

const parseJsonObjectText = (value: unknown, label: string): Record<string, any> | null => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') throw new Error('invalid');
    return parsed;
  } catch {
    throw new Error(`${label} must be a valid JSON object`);
  }
};

const parseJsonArrayText = (value: unknown, label: string): any[] | null => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (!Array.isArray(parsed)) throw new Error('invalid');
    return parsed;
  } catch {
    throw new Error(`${label} must be a valid JSON array`);
  }
};
const safeParseJsonObjectText = (value: unknown): { value: Record<string, any> | null; error?: string } => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) return { value: null };
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
      return { value: null, error: 'JSON must be an object.' };
    }
    return { value: parsed };
  } catch {
    return { value: null, error: 'Invalid JSON.' };
  }
};

const safeParseJsonArrayText = (value: unknown): { value: any[] | null; error?: string } => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) return { value: null };
  try {
    const parsed = JSON.parse(trimmed);
    if (!Array.isArray(parsed)) {
      return { value: null, error: 'JSON must be an array.' };
    }
    return { value: parsed };
  } catch {
    return { value: null, error: 'Invalid JSON.' };
  }
};

const schemaPropertyEntries = (schema?: Record<string, any> | null) => {
  const properties = schema && typeof schema === 'object' && !Array.isArray(schema) ? schema.properties : null;
  return properties && typeof properties === 'object' && !Array.isArray(properties)
    ? Object.entries(properties as Record<string, any>)
    : [];
};

const SchemaSummary: React.FC<{ title: string; schema?: Record<string, any> | null; emptyText: string; error?: string }> = ({ title, schema, emptyText, error }) => {
  const entries = schemaPropertyEntries(schema);
  const required: string[] = Array.isArray(schema?.required) ? (schema?.required as string[]) : [];
  return (
    <Card size="small" title={title} style={{ width: '100%' }}>
      {error ? (
        <Alert type="error" showIcon message={error} />
      ) : !schema ? (
        <Text type="secondary">{emptyText}</Text>
      ) : (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Space wrap size={[6, 6]}>
            <Tag color="blue">type: {String(schema.type || 'object')}</Tag>
            <Tag>{entries.length} properties</Tag>
            {required.length > 0 && <Tag color="orange">{required.length} required</Tag>}
          </Space>
          {entries.length > 0 ? (
            <Table
              size="small"
              pagination={false}
              rowKey="key"
              dataSource={entries.map(([key, prop]: [string, any]) => ({
                key,
                type: prop?.type || (prop?.enum ? 'enum' : 'any'),
                title: prop?.title || key,
                required: required.includes(key),
                description: prop?.description || '',
              }))}
              columns={[
                { title: 'Field', dataIndex: 'title', key: 'title', render: (value: string, row: any) => <Space><Text strong>{value}</Text><Tag>{row.key}</Tag></Space> },
                { title: 'Type', dataIndex: 'type', key: 'type', width: 120, render: (value: string) => <Tag>{value}</Tag> },
                { title: 'Required', dataIndex: 'required', key: 'required', width: 90, render: (value: boolean) => value ? <Tag color="orange">Yes</Tag> : <Tag>No</Tag> },
                { title: 'Description', dataIndex: 'description', key: 'description', render: (value: string) => value || <Text type="secondary">-</Text> },
              ]}
            />
          ) : (
            <Text type="secondary">No properties defined.</Text>
          )}
        </Space>
      )}
    </Card>
  );
};

const JsonObjectSummary: React.FC<{ title: string; value?: Record<string, any> | null; emptyText: string; error?: string }> = ({ title, value, emptyText, error }) => {
  const entries = value && typeof value === 'object' && !Array.isArray(value) ? Object.entries(value) : [];
  return (
    <Card size="small" title={title} style={{ width: '100%' }}>
      {error ? (
        <Alert type="error" showIcon message={error} />
      ) : !value ? (
        <Text type="secondary">{emptyText}</Text>
      ) : entries.length > 0 ? (
        <Table
          size="small"
          pagination={false}
          rowKey="key"
          dataSource={entries.map(([key, item]) => {
            const valueType = Array.isArray(item) ? 'array' : item === null ? 'null' : typeof item;
            const preview = typeof item === 'object' && item !== null ? JSON.stringify(item) : String(item ?? '');
            return { key, valueType, preview };
          })}
          columns={[
            { title: 'Key', dataIndex: 'key', key: 'key', render: (value: string) => <Text strong>{value}</Text> },
            { title: 'Type', dataIndex: 'valueType', key: 'valueType', width: 120, render: (value: string) => <Tag>{value}</Tag> },
            { title: 'Preview', dataIndex: 'preview', key: 'preview', render: (value: string) => <Text type="secondary">{value.length > 120 ? `${value.slice(0, 120)}...` : value}</Text> },
          ]}
        />
      ) : (
        <Text type="secondary">No keys defined.</Text>
      )}
    </Card>
  );
};

const CommandSpecsSummary: React.FC<{ value?: any[] | null; error?: string }> = ({ value, error }) => {
  const commands = Array.isArray(value) ? value : [];
  return (
    <Card size="small" title="Command specs" style={{ width: '100%' }}>
      {error ? (
        <Alert type="error" showIcon message={error} />
      ) : commands.length > 0 ? (
        <Table
          size="small"
          pagination={false}
          rowKey={(record, index) => String(record?.key || index)}
          dataSource={commands.map((command, index) => ({
            index,
            key: command?.key || `command_${index + 1}`,
            name: command?.name || command?.label || '',
            params: command?.parameters || command?.params || command?.parameterSchema?.properties || null,
            executionMapping: command?.executionMapping || null,
          }))}
          columns={[
            { title: '#', dataIndex: 'index', key: 'index', width: 48, render: (value: number) => value + 1 },
            { title: 'Key', dataIndex: 'key', key: 'key', render: (value: string) => <Tag>{value}</Tag> },
            { title: 'Name', dataIndex: 'name', key: 'name', render: (value: string) => value || <Text type="secondary">-</Text> },
            {
              title: 'Params',
              dataIndex: 'params',
              key: 'params',
              width: 100,
              render: (value: any) => {
                const count = value && typeof value === 'object' ? Object.keys(value).length : 0;
                return count > 0 ? <Tag color="blue">{count}</Tag> : <Text type="secondary">-</Text>;
              },
            },
            { title: 'Mapping', dataIndex: 'executionMapping', key: 'executionMapping', width: 120, render: (value: any) => value ? <Tag color="green">configured</Tag> : <Text type="secondary">-</Text> },
          ]}
        />
      ) : (
        <Text type="secondary">No commands defined. Use Advanced JSON to add command definitions.</Text>
      )}
    </Card>
  );
};


const capabilityCategoryColor: Record<string, string> = {
  navigation: 'blue',
  mapping: 'purple',
  autonomy: 'cyan',
  localization: 'geekblue',
  safety: 'gold',
  payload: 'volcano',
  connectivity: 'green',
  other: 'default',
};

const ModuleCatalogueTab: React.FC<Props> = ({ buildAuthHeaders, onAuthMismatch }) => {
  const [items, setItems] = useState<CapDef[]>([]);
  const [capabilityItems, setCapabilityItems] = useState<CapabilityDef[]>([]);
  const [loading, setLoading] = useState(false);
  const [capabilityLoading, setCapabilityLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [capabilityModalOpen, setCapabilityModalOpen] = useState(false);
  const [editing, setEditing] = useState<CapDef | null>(null);
  const [editingCapability, setEditingCapability] = useState<CapabilityDef | null>(null);
  const [saving, setSaving] = useState(false);
  const [capabilitySaving, setCapabilitySaving] = useState(false);
  const [schemasAdvanced, setSchemasAdvanced] = useState(false);
  const [commandsAdvanced, setCommandsAdvanced] = useState(false);
  const [metadataAdvanced, setMetadataAdvanced] = useState(false);
  const [form] = Form.useForm();
  const [capabilityForm] = Form.useForm();
  const configSchemaText = Form.useWatch('configSchemaText', form);
  const defaultConfigText = Form.useWatch('defaultConfigText', form);
  const outputSchemaText = Form.useWatch('outputSchemaText', form);
  const commandSpecsText = Form.useWatch('commandSpecsText', form);
  const mavlinkMappingText = Form.useWatch('mavlinkMappingText', form);
  const metaText = Form.useWatch('metaText', form);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(ENDPOINTS.MODULE_DEFINITIONS, {
        headers: buildAuthHeaders(), credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setItems(Array.isArray(data?.data) ? data.data : []);
      } else {
        const err = await res.json();
        if (await onAuthMismatch(res, err)) return;
        message.error(err?.message || 'Failed to load modules');
      }
    } catch { message.error('Failed to load modules'); }
    finally { setLoading(false); }
  }, [buildAuthHeaders, onAuthMismatch]);

  const fetchCapabilities = useCallback(async () => {
    setCapabilityLoading(true);
    try {
      const res = await fetch(ENDPOINTS.DEVICE_CAPABILITY_DEFINITIONS, {
        headers: buildAuthHeaders(), credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setCapabilityItems(Array.isArray(data?.data) ? data.data : []);
      } else {
        const err = await res.json();
        if (await onAuthMismatch(res, err)) return;
        message.error(err?.message || 'Failed to load capabilities');
      }
    } catch { message.error('Failed to load capabilities'); }
    finally { setCapabilityLoading(false); }
  }, [buildAuthHeaders, onAuthMismatch]);

  useEffect(() => { fetchAll(); fetchCapabilities(); }, [fetchAll, fetchCapabilities]);

  const openCreate = useCallback(() => {
    setEditing(null);
    form.resetFields();
    setSchemasAdvanced(false);
    setCommandsAdvanced(false);
    setMetadataAdvanced(false);
    form.setFieldsValue({
      moduleType: 'sensor', defaultTriggerMethod: 'api_call',
      dataType: 'scalar', supportsContinuous: false, sortOrder: 99,
      configSchemaText: '',
      defaultConfigText: '',
      commandSpecsText: '',
      outputSchemaText: '',
      mavlinkMappingText: '',
      metaText: '',
    });
    setModalOpen(true);
  }, [form]);

  const openEdit = useCallback((item: CapDef) => {
    setEditing(item);
    setSchemasAdvanced(false);
    setCommandsAdvanced(false);
    setMetadataAdvanced(false);
    form.setFieldsValue({
      key: item.key,
      name: item.name,
      description: item.description ?? '',
      moduleType: item.moduleType,
      defaultTriggerMethod: item.defaultTriggerMethod,
      dataType: item.dataType,
      unit: item.unit ?? '',
      supportsContinuous: item.supportsContinuous,
      icon: item.icon ?? '',
      sortOrder: item.sortOrder,
      configSchemaText: stringifyJson(item.configSchema),
      defaultConfigText: stringifyJson(item.defaultConfig),
      commandSpecsText: stringifyJson(item.commandSpecs),
      outputSchemaText: stringifyJson(item.outputSchema),
      mavlinkMappingText: stringifyJson(item.mavlinkMapping),
      metaText: stringifyJson(item.meta),
    });
    setModalOpen(true);
  }, [form]);

  const openCreateCapability = useCallback(() => {
    setEditingCapability(null);
    capabilityForm.resetFields();
    capabilityForm.setFieldsValue({
      category: 'navigation',
      operationEnvironments: [],
      sortOrder: 99,
    });
    setCapabilityModalOpen(true);
  }, [capabilityForm]);

  const openEditCapability = useCallback((item: CapabilityDef) => {
    setEditingCapability(item);
    capabilityForm.setFieldsValue({
      key: item.key,
      name: item.name,
      description: item.description ?? '',
      category: item.category || 'other',
      operationEnvironments: item.operationEnvironments || [],
      icon: item.icon ?? '',
      sortOrder: item.sortOrder,
    });
    setCapabilityModalOpen(true);
  }, [capabilityForm]);

  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const url = editing
        ? `${ENDPOINTS.MODULE_DEFINITIONS}/${editing.id}`
        : ENDPOINTS.MODULE_DEFINITIONS;
      const method = editing ? 'PUT' : 'POST';

      const body: Record<string, any> = {
        name: String(values.name || '').trim(),
        description: String(values.description || '').trim() || null,
        moduleType: values.moduleType,
        defaultTriggerMethod: values.defaultTriggerMethod,
        dataType: values.dataType,
        unit: String(values.unit || '').trim() || null,
        supportsContinuous: values.supportsContinuous === true,
        icon: String(values.icon || '').trim() || null,
        sortOrder: typeof values.sortOrder === 'number' ? values.sortOrder : 99,
        configSchema: parseJsonObjectText(values.configSchemaText, 'Config schema'),
        defaultConfig: parseJsonObjectText(values.defaultConfigText, 'Default config'),
        commandSpecs: parseJsonArrayText(values.commandSpecsText, 'Command specs'),
        outputSchema: parseJsonObjectText(values.outputSchemaText, 'Output schema'),
        mavlinkMapping: parseJsonObjectText(values.mavlinkMappingText, 'MAVLink mapping'),
        meta: parseJsonObjectText(values.metaText, 'Metadata'),
      };
      if (!editing || !editing.isBuiltIn) {
        body.key = String(values.key || '').trim();
      }

      const res = await fetch(url, {
        method, headers: buildAuthHeaders(), credentials: 'include',
        body: JSON.stringify(body),
      });

      if (res.ok || res.status === 201) {
        message.success(editing ? 'Module updated' : 'Module created');
        setModalOpen(false);
        fetchAll();
      } else {
        const err = await res.json();
        if (await onAuthMismatch(res, err)) return;
        message.error(err?.error || err?.message || 'Failed to save');
      }
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e instanceof Error ? e.message : 'Failed to save');
    } finally { setSaving(false); }
  }, [buildAuthHeaders, editing, fetchAll, form, onAuthMismatch]);

  const handleDelete = useCallback(async (item: CapDef) => {
    try {
      const res = await fetch(`${ENDPOINTS.MODULE_DEFINITIONS}/${item.id}`, {
        method: 'DELETE', headers: buildAuthHeaders(), credentials: 'include',
      });
      if (res.ok || res.status === 204) {
        message.success('Module deleted');
        fetchAll();
      } else {
        const err = await res.json();
        if (await onAuthMismatch(res, err)) return;
        message.error(err?.error || 'Failed to delete');
      }
    } catch { message.error('Failed to delete'); }
  }, [buildAuthHeaders, fetchAll, onAuthMismatch]);

  const enableAdvancedSchemas = useCallback(() => {
    Modal.confirm({
      title: 'Use advanced JSON editor?',
      content: 'Raw schema JSON edits can break module configuration forms and emitted data handling. Use this only when you need to change the schema structure directly.',
      okText: 'Use Advanced JSON',
      cancelText: 'Stay in simple view',
      onOk: () => setSchemasAdvanced(true),
    });
  }, []);

  const returnToSimpleSchemas = useCallback(() => {
    try {
      parseJsonObjectText(form.getFieldValue('configSchemaText'), 'Config schema');
      parseJsonObjectText(form.getFieldValue('defaultConfigText'), 'Default config');
      parseJsonObjectText(form.getFieldValue('outputSchemaText'), 'Output schema');
      setSchemasAdvanced(false);
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Invalid JSON');
    }
  }, [form]);

  const enableAdvancedCommands = useCallback(() => {
    Modal.confirm({
      title: 'Use advanced command JSON editor?',
      content: 'Raw command spec edits can break command execution mappings. Use this only when you need to change command structure directly.',
      okText: 'Use Advanced JSON',
      cancelText: 'Stay in simple view',
      onOk: () => setCommandsAdvanced(true),
    });
  }, []);

  const returnToSimpleCommands = useCallback(() => {
    try {
      parseJsonArrayText(form.getFieldValue('commandSpecsText'), 'Command specs');
      setCommandsAdvanced(false);
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Invalid JSON');
    }
  }, [form]);

  const enableAdvancedMetadata = useCallback(() => {
    Modal.confirm({
      title: 'Use advanced metadata JSON editor?',
      content: 'Raw metadata edits can break MAVLink mappings or display metadata. Use this only when you need to change metadata structure directly.',
      okText: 'Use Advanced JSON',
      cancelText: 'Stay in simple view',
      onOk: () => setMetadataAdvanced(true),
    });
  }, []);

  const returnToSimpleMetadata = useCallback(() => {
    try {
      parseJsonObjectText(form.getFieldValue('mavlinkMappingText'), 'MAVLink mapping');
      parseJsonObjectText(form.getFieldValue('metaText'), 'Metadata');
      setMetadataAdvanced(false);
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Invalid JSON');
    }
  }, [form]);


  const handleSaveCapability = useCallback(async () => {
    try {
      const values = await capabilityForm.validateFields();
      setCapabilitySaving(true);
      const url = editingCapability
        ? `${ENDPOINTS.DEVICE_CAPABILITY_DEFINITIONS}/${editingCapability.id}`
        : ENDPOINTS.DEVICE_CAPABILITY_DEFINITIONS;
      const method = editingCapability ? 'PUT' : 'POST';

      const body: Record<string, any> = {
        name: String(values.name || '').trim(),
        description: String(values.description || '').trim() || null,
        category: values.category || 'other',
        operationEnvironments: Array.isArray(values.operationEnvironments) ? values.operationEnvironments : [],
        icon: String(values.icon || '').trim() || null,
        sortOrder: typeof values.sortOrder === 'number' ? values.sortOrder : 99,
      };
      if (!editingCapability || !editingCapability.isBuiltIn) {
        body.key = String(values.key || '').trim();
      }

      const res = await fetch(url, {
        method, headers: buildAuthHeaders(), credentials: 'include',
        body: JSON.stringify(body),
      });

      if (res.ok || res.status === 201) {
        message.success(editingCapability ? 'Capability updated' : 'Capability created');
        setCapabilityModalOpen(false);
        fetchCapabilities();
      } else {
        const err = await res.json();
        if (await onAuthMismatch(res, err)) return;
        message.error(err?.error || err?.message || 'Failed to save capability');
      }
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e instanceof Error ? e.message : 'Failed to save capability');
    } finally { setCapabilitySaving(false); }
  }, [buildAuthHeaders, capabilityForm, editingCapability, fetchCapabilities, onAuthMismatch]);

  const handleDeleteCapability = useCallback(async (item: CapabilityDef) => {
    try {
      const res = await fetch(`${ENDPOINTS.DEVICE_CAPABILITY_DEFINITIONS}/${item.id}`, {
        method: 'DELETE', headers: buildAuthHeaders(), credentials: 'include',
      });
      if (res.ok || res.status === 204) {
        message.success('Capability deleted');
        fetchCapabilities();
      } else {
        const err = await res.json();
        if (await onAuthMismatch(res, err)) return;
        message.error(err?.error || 'Failed to delete capability');
      }
    } catch { message.error('Failed to delete capability'); }
  }, [buildAuthHeaders, fetchCapabilities, onAuthMismatch]);

  const columns = useMemo<ColumnsType<CapDef>>(() => [
    {
      title: 'Name', dataIndex: 'name', key: 'name',
      render: (v: string, r) => (
        <Space>
          <Text strong>{v}</Text>
          {r.isBuiltIn && <Tag icon={<LockOutlined />} color="default">Built-in</Tag>}
        </Space>
      ),
    },
    { title: 'Key', dataIndex: 'key', key: 'key', render: (v: string) => <Tag>{v}</Tag> },
    {
      title: 'Type', dataIndex: 'moduleType', key: 'type',
      render: (v: string) => <Tag color={typeColor[v] || 'default'}>{v}</Tag>,
      filters: CAP_TYPES.map((t) => ({ text: t.label, value: t.value })),
      onFilter: (value, record) => record.moduleType === value,
    },
    { title: 'Trigger', dataIndex: 'defaultTriggerMethod', key: 'trigger', render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Data', dataIndex: 'dataType', key: 'data', render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', render: (v?: string | null) => v || <Text type="secondary">—</Text> },
    {
      title: 'Specs', key: 'specs', width: 150,
      render: (_: unknown, r) => (
        <Space wrap size={[4, 4]}>
          {Array.isArray(r.commandSpecs) && r.commandSpecs.length > 0 && <Tag color="geekblue">{r.commandSpecs.length} commands</Tag>}
          {r.configSchema && <Tag color="green">config</Tag>}
          {r.outputSchema && <Tag color="purple">output</Tag>}
          {!r.commandSpecs?.length && !r.configSchema && !r.outputSchema && <Text type="secondary">—</Text>}
        </Space>
      ),
    },
    {
      title: 'Continuous', dataIndex: 'supportsContinuous', key: 'cont', width: 90,
      render: (v: boolean) => v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>,
    },
    { title: 'Order', dataIndex: 'sortOrder', key: 'sort', width: 70 },
    {
      title: '', key: 'actions', width: 100, align: 'right' as const,
      render: (_: unknown, record: CapDef) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm
            title={`Delete ${record.name}?`}
            description="This removes it from device type templates and physical devices, and built-ins will not be reseeded."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ], [handleDelete, openEdit]);

  const renderSchemasTab = () => {
    const configSchemaResult = safeParseJsonObjectText(configSchemaText);
    const defaultConfigResult = safeParseJsonObjectText(defaultConfigText);
    const outputSchemaResult = safeParseJsonObjectText(outputSchemaText);
    const configSchema = configSchemaResult.value;
    const defaultConfig = defaultConfigResult.value || {};

    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }} align="center">
          <Text type="secondary">
            Simple view protects schema JSON from accidental structural edits. Use advanced mode only for raw schema changes.
          </Text>
          {schemasAdvanced ? (
            <Button onClick={returnToSimpleSchemas}>Back to simple view</Button>
          ) : (
            <Button danger onClick={enableAdvancedSchemas}>Advanced JSON</Button>
          )}
        </Space>

        <Form.Item name="configSchemaText" hidden={!schemasAdvanced} label={schemasAdvanced ? 'Config schema JSON' : undefined} extra={schemasAdvanced ? 'JSON object used to render per-device module configuration forms.' : undefined}>
          <Input.TextArea rows={10} placeholder={'{\n  "type": "object",\n  "properties": {}\n}'} />
        </Form.Item>
        <Form.Item name="defaultConfigText" hidden={!schemasAdvanced} label={schemasAdvanced ? 'Default config JSON' : undefined} extra={schemasAdvanced ? 'JSON object copied into device type templates and device modules as defaults.' : undefined}>
          <Input.TextArea rows={8} placeholder={'{\n  "targetDepthMm": 100\n}'} />
        </Form.Item>
        <Form.Item name="outputSchemaText" hidden={!schemasAdvanced} label={schemasAdvanced ? 'Output schema JSON' : undefined} extra={schemasAdvanced ? 'JSON object describing the data emitted by this module.' : undefined}>
          <Input.TextArea rows={8} placeholder={'{\n  "type": "object",\n  "properties": {}\n}'} />
        </Form.Item>

        {schemasAdvanced ? (
          <Alert
            type="warning"
            showIcon
            message="Advanced JSON editor"
            description="Only save valid JSON objects. Changing schema keys, types, required fields, or nesting can break device module forms."
          />
        ) : (
          <>
            <SchemaSummary
              title="Config schema"
              schema={configSchema}
              error={configSchemaResult.error}
              emptyText="No config schema defined. Add one in Advanced JSON mode to enable the default config form."
            />
            <Card size="small" title="Default config" style={{ width: '100%' }}>
              {configSchemaResult.error ? (
                <Alert type="error" showIcon message="Fix the config schema in Advanced JSON mode before editing defaults." />
              ) : !configSchema ? (
                <Text type="secondary">No config schema available. Default config can only be edited in Advanced JSON mode.</Text>
              ) : defaultConfigResult.error ? (
                <Alert type="error" showIcon message={defaultConfigResult.error} />
              ) : (
                <ModuleConfigEditor
                  schema={configSchema}
                  defaultValue={{}}
                  value={defaultConfig}
                  onChange={(nextValue) => form.setFieldValue('defaultConfigText', stringifyJson(nextValue || {}))}
                />
              )}
            </Card>
            <SchemaSummary
              title="Output schema"
              schema={outputSchemaResult.value}
              error={outputSchemaResult.error}
              emptyText="No output schema defined."
            />
          </>
        )}
      </Space>
    );
  };

  const renderCommandsTab = () => {
    const commandSpecsResult = safeParseJsonArrayText(commandSpecsText);

    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }} align="center">
          <Text type="secondary">
            Simple view shows command definitions without exposing raw JSON. Use advanced mode for structural edits.
          </Text>
          {commandsAdvanced ? (
            <Button onClick={returnToSimpleCommands}>Back to simple view</Button>
          ) : (
            <Button danger onClick={enableAdvancedCommands}>Advanced JSON</Button>
          )}
        </Space>

        <Form.Item
          label={commandsAdvanced ? 'Command specs JSON' : undefined}
          name="commandSpecsText"
          hidden={!commandsAdvanced}
          extra={commandsAdvanced ? 'JSON array of command definitions, parameter schemas, and execution mappings.' : undefined}
        >
          <Input.TextArea rows={18} placeholder={'[\n  {\n    "key": "capture",\n    "name": "Capture",\n    "executionMapping": {}\n  }\n]'} />
        </Form.Item>

        {commandsAdvanced ? (
          <Alert
            type="warning"
            showIcon
            message="Advanced command JSON editor"
            description="Only save valid JSON arrays. Changing command keys, params, or execution mappings can break mission command execution."
          />
        ) : (
          <CommandSpecsSummary value={commandSpecsResult.value} error={commandSpecsResult.error} />
        )}
      </Space>
    );
  };

  const renderMetadataTab = () => {
    const mavlinkMappingResult = safeParseJsonObjectText(mavlinkMappingText);
    const metaResult = safeParseJsonObjectText(metaText);

    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }} align="center">
          <Text type="secondary">
            Simple view summarizes mapping and display metadata. Use advanced mode for raw object edits.
          </Text>
          {metadataAdvanced ? (
            <Button onClick={returnToSimpleMetadata}>Back to simple view</Button>
          ) : (
            <Button danger onClick={enableAdvancedMetadata}>Advanced JSON</Button>
          )}
        </Space>

        <Form.Item
          label={metadataAdvanced ? 'MAVLink mapping JSON' : undefined}
          name="mavlinkMappingText"
          hidden={!metadataAdvanced}
          extra={metadataAdvanced ? 'Legacy/simple MAVLink mapping object where needed.' : undefined}
        >
          <Input.TextArea rows={8} placeholder={'{\n  "commandCode": 203,\n  "paramMapping": {}\n}'} />
        </Form.Item>
        <Form.Item
          label={metadataAdvanced ? 'Display/source metadata JSON' : undefined}
          name="metaText"
          hidden={!metadataAdvanced}
          extra={metadataAdvanced ? 'Display hints and source notes only, not per-device operational config.' : undefined}
        >
          <Input.TextArea rows={8} placeholder={'{\n  "sourceUrl": "https://mavlink.io/"\n}'} />
        </Form.Item>

        {metadataAdvanced ? (
          <Alert
            type="warning"
            showIcon
            message="Advanced metadata JSON editor"
            description="Only save valid JSON objects. Changing MAVLink mapping keys or metadata structure can break module behavior."
          />
        ) : (
          <>
            <JsonObjectSummary
              title="MAVLink mapping"
              value={mavlinkMappingResult.value}
              error={mavlinkMappingResult.error}
              emptyText="No MAVLink mapping defined."
            />
            <JsonObjectSummary
              title="Display/source metadata"
              value={metaResult.value}
              error={metaResult.error}
              emptyText="No display or source metadata defined."
            />
          </>
        )}
      </Space>
    );
  };

  const capabilityColumns = useMemo<ColumnsType<CapabilityDef>>(() => [
    {
      title: 'Name', dataIndex: 'name', key: 'name',
      render: (v: string, r) => (
        <Space>
          <Text strong>{v}</Text>
          {r.isBuiltIn && <Tag icon={<LockOutlined />} color="default">Built-in</Tag>}
        </Space>
      ),
    },
    { title: 'Key', dataIndex: 'key', key: 'key', render: (v: string) => <Tag>{v}</Tag> },
    {
      title: 'Category', dataIndex: 'category', key: 'category',
      render: (v: string) => <Tag color={capabilityCategoryColor[v] || 'default'}>{v}</Tag>,
      filters: CAPABILITY_CATEGORIES.map((t) => ({ text: t.label, value: t.value })),
      onFilter: (value, record) => record.category === value,
    },
    {
      title: 'Environments', dataIndex: 'operationEnvironments', key: 'operationEnvironments',
      render: (values?: string[] | null) => (
        <Space wrap size={[4, 4]}>
          {(values || []).length > 0
            ? values!.map((value) => <Tag key={value}>{value}</Tag>)
            : <Text type="secondary">Any</Text>}
        </Space>
      ),
    },
    { title: 'Order', dataIndex: 'sortOrder', key: 'sort', width: 70 },
    {
      title: '', key: 'actions', width: 100, align: 'right' as const,
      render: (_: unknown, record: CapabilityDef) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditCapability(record)} />
          {!record.isBuiltIn && (
            <Popconfirm title="Delete this capability?" onConfirm={() => handleDeleteCapability(record)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ], [handleDeleteCapability, openEditCapability]);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card size="small">
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <div>
            <Title level={5} style={{ margin: 0 }}>Device Modules and Capabilities</Title>
            <Text type="secondary">
              {items.length} module definitions and {capabilityItems.length} platform capabilities
            </Text>
          </div>
        </Space>
      </Card>

      <Tabs
        items={[
          {
            key: 'modules',
            label: 'Device modules',
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                  <Text type="secondary">
                    Triggerable hardware/software components such as cameras, probes, relays, and sensors.
                  </Text>
                  <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                    Add module
                  </Button>
                </Space>
                <Table
                  rowKey="id"
                  dataSource={items}
                  columns={columns}
                  loading={loading}
                  pagination={false}
                  size="small"
                />
              </Space>
            ),
          },
          {
            key: 'capabilities',
            label: 'Capabilities',
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                  <Text type="secondary">
                    Platform abilities used for mission eligibility, such as SLAM creation, GPS navigation, and docking.
                  </Text>
                  <Button type="primary" icon={<PlusOutlined />} onClick={openCreateCapability}>
                    Add capability
                  </Button>
                </Space>
                <Table
                  rowKey="id"
                  dataSource={capabilityItems}
                  columns={capabilityColumns}
                  loading={capabilityLoading}
                  pagination={false}
                  size="small"
                />
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={editing ? `Edit: ${editing.name}` : 'New Module Definition'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        okText={editing ? 'Save changes' : 'Create'}
        width={1000}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Tabs
            items={[
              {
                key: 'basic',
                label: 'Basics',
                children: (
                  <>
                    <Form.Item label="Key" name="key" rules={[{ required: true }, { max: 80 }]}
                      extra={editing?.isBuiltIn ? 'Key is locked for built-in definitions.' : 'Machine-readable identifier, e.g. "wind_speed"'}
                    >
                      <Input disabled={!!editing?.isBuiltIn} placeholder="wind_speed" />
                    </Form.Item>

                    <Form.Item label="Name" name="name" rules={[{ required: true }, { max: 120 }]}>
                      <Input placeholder="Wind Speed Sensor" />
                    </Form.Item>

                    <Form.Item label="Description" name="description" rules={[{ max: 500 }]}>
                      <Input.TextArea rows={2} placeholder="Short description of this module" />
                    </Form.Item>

                    <Space size="middle" style={{ width: '100%' }} wrap>
                      <Form.Item label="Category" name="moduleType" style={{ minWidth: 180 }}>
                        <Select options={CAP_TYPES} />
                      </Form.Item>
                      <Form.Item label="Default trigger" name="defaultTriggerMethod" style={{ minWidth: 320 }}>
                        <Select options={TRIGGER_METHODS} popupMatchSelectWidth={false} dropdownStyle={{ minWidth: 420 }} />
                      </Form.Item>
                    </Space>

                    <Space size="middle" style={{ width: '100%' }} wrap>
                      <Form.Item label="Data type" name="dataType" style={{ minWidth: 220 }}>
                        <Select options={DATA_TYPES} popupMatchSelectWidth={false} dropdownStyle={{ minWidth: 280 }} />
                      </Form.Item>
                      <Form.Item label="Unit" name="unit" style={{ minWidth: 120 }}>
                        <Input placeholder="m/s" />
                      </Form.Item>
                    </Space>

                    <Space size="middle" style={{ width: '100%' }} wrap>
                      <Form.Item label="Supports continuous" name="supportsContinuous" valuePropName="checked">
                        <Switch />
                      </Form.Item>
                      <Form.Item label="Sort order" name="sortOrder">
                        <InputNumber min={0} max={999} />
                      </Form.Item>
                      <Form.Item label="Icon (Ant Design)" name="icon">
                        <Input placeholder="ExperimentOutlined" style={{ width: 200 }} />
                      </Form.Item>
                    </Space>
                  </>
                ),
              },
              {
                key: 'schemas',
                label: 'Schemas',
                children: renderSchemasTab(),
              },
              {
                key: 'commands',
                label: 'Commands',
                children: renderCommandsTab(),
              },
              {
                key: 'metadata',
                label: 'Metadata',
                children: renderMetadataTab(),
              },
            ]}
          />
        </Form>
      </Modal>

      <Modal
        title={editingCapability ? `Edit: ${editingCapability.name}` : 'New Capability Definition'}
        open={capabilityModalOpen}
        onCancel={() => setCapabilityModalOpen(false)}
        onOk={handleSaveCapability}
        confirmLoading={capabilitySaving}
        okText={editingCapability ? 'Save changes' : 'Create'}
        width={600}
        destroyOnClose
      >
        <Form form={capabilityForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item label="Key" name="key" rules={[{ required: true }, { max: 80 }]}
            extra={editingCapability?.isBuiltIn ? 'Key is locked for built-in definitions.' : 'Machine-readable identifier, e.g. "thermal_mapping"'}
          >
            <Input disabled={!!editingCapability?.isBuiltIn} placeholder="thermal_mapping" />
          </Form.Item>

          <Form.Item label="Name" name="name" rules={[{ required: true }, { max: 120 }]}>
            <Input placeholder="Thermal mapping" />
          </Form.Item>

          <Form.Item label="Description" name="description" rules={[{ max: 500 }]}>
            <Input.TextArea rows={2} placeholder="Short description of this platform capability" />
          </Form.Item>

          <Space size="middle" style={{ width: '100%' }} wrap>
            <Form.Item label="Category" name="category" style={{ minWidth: 200 }}>
              <Select options={CAPABILITY_CATEGORIES} />
            </Form.Item>
            <Form.Item label="Operation environments" name="operationEnvironments" style={{ minWidth: 280 }}>
              <Select mode="multiple" allowClear options={OPERATION_ENVIRONMENTS} />
            </Form.Item>
          </Space>

          <Space size="middle" style={{ width: '100%' }} wrap>
            <Form.Item label="Sort order" name="sortOrder">
              <InputNumber min={0} max={999} />
            </Form.Item>
            <Form.Item label="Icon (Ant Design)" name="icon">
              <Input placeholder="CompassOutlined" style={{ width: 220 }} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </Space>
  );
};

export default ModuleCatalogueTab;
